import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BUSINESS_TOOL_SECTIONS, BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';
import { t } from 'i18next';

import { initialPeriodsTableRow, IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';

import { concatWithDot } from 'helpers/string';
import { ObjectValues } from 'helpers/types';

import { DirectForm } from '../DirectForm';
import { TOTAL_FIELDS } from '../DirectForm/DirectFormComponents/DirectFormTable/utils/form';
import {
  financingActivitiesInitialValues,
  IFinancingActivitiesType,
} from '../DirectForm/DirectFormComponents/Sections/FinancingActivities/utils/form';
import {
  IInvestingActivitiesType,
  investingActivitiesInitialValues,
} from '../DirectForm/DirectFormComponents/Sections/InvestingActivities/utils/form';
import {
  IOperatingActivitiesType,
  OPERATING_ACTIVITIES_NAMES,
  operatingActivitiesInitialValues,
} from '../DirectForm/DirectFormComponents/Sections/OperatingActivities/utils/form';
import { InDirectForm } from '../InDirectForm';
import { INDIRECT_TOTAL_FIELDS } from '../InDirectForm/InDirectFormComponents/InDirectFormTable/utils/form';
import {
  adjustmentsForInitialValues,
  IAdjustmentsForInitialValues,
} from '../InDirectForm/InDirectFormComponents/Sections/AdjustmentsForForm/utils/form';
import {
  IOperatingAssetsAndLiabilitiesInitialValues,
  operatingAssetsAndLiabilitiesInitialValues,
} from '../InDirectForm/InDirectFormComponents/Sections/OperatingAssetsAndLiabilitiesForm/utils/form';
import {
  IOtherItemsInitialValues,
  otherItemsInitialValues,
} from '../InDirectForm/InDirectFormComponents/Sections/OtherItemsForm/utils/form';

export const FIRST_COLUMN_WIDTH = 210;

export const projectedCashFlowPrefix =
  `${BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN}.${BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PROJECTED_CASH_FLOW}` as const;

export const useGetOperatingActivitiesRows = (isExamplePage: boolean) => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const operatingActivitiesFields = [
      {
        name: OPERATING_ACTIVITIES_NAMES.CASH_RECEIVED_CUSTOMERS,
        title: isExamplePage
          ? 'Cash received from customers'
          : t('financialPlan.projectedCashFlow.direct.operatingActivities.cashReceivedCustomers'),
      },
      {
        name: OPERATING_ACTIVITIES_NAMES.CASH_PAID_SUPPLIERS,
        title: isExamplePage
          ? 'Cash paid to suppliers'
          : t('financialPlan.projectedCashFlow.direct.operatingActivities.cashPaidSuppliers'),
      },
      {
        name: OPERATING_ACTIVITIES_NAMES.CASH_PAID_EMPLOYEES,
        title: isExamplePage
          ? 'Cash paid to employees'
          : t('financialPlan.projectedCashFlow.direct.operatingActivities.cashPaidEmployees'),
      },
      {
        name: OPERATING_ACTIVITIES_NAMES.CASH_PAID_OTHER_OPERATING_EXPENSES,
        title: isExamplePage
          ? 'Cash paid for other operating expenses'
          : t('financialPlan.projectedCashFlow.direct.operatingActivities.cashPaidOtherOperatingExpenses'),
      },
      {
        name: OPERATING_ACTIVITIES_NAMES.INTEREST_RECEIVED,
        title: isExamplePage
          ? 'Interest received'
          : t('financialPlan.projectedCashFlow.direct.operatingActivities.interestReceived'),
      },
      {
        name: OPERATING_ACTIVITIES_NAMES.INTEREST_PAID,
        title: isExamplePage
          ? 'Interest paid'
          : t('financialPlan.projectedCashFlow.direct.operatingActivities.interestPaid'),
      },
      {
        name: OPERATING_ACTIVITIES_NAMES.INCOME_TAXES_PAID,
        title: isExamplePage
          ? 'Income taxes paid'
          : t('financialPlan.projectedCashFlow.direct.operatingActivities.incomeTaxesPaid'),
      },
    ];

    return operatingActivitiesFields;
  }, [t, i18n, isExamplePage]);
};

export const operatingActivitiesRows = [
  {
    name: OPERATING_ACTIVITIES_NAMES.CASH_RECEIVED_CUSTOMERS,
    title: t('financialPlan.projectedCashFlow.direct.operatingActivities.cashReceivedCustomers'),
  },
  {
    name: OPERATING_ACTIVITIES_NAMES.CASH_PAID_SUPPLIERS,
    title: t('financialPlan.projectedCashFlow.direct.operatingActivities.cashPaidSuppliers'),
  },
  {
    name: OPERATING_ACTIVITIES_NAMES.CASH_PAID_EMPLOYEES,
    title: t('financialPlan.projectedCashFlow.direct.operatingActivities.cashPaidEmployees'),
  },
  {
    name: OPERATING_ACTIVITIES_NAMES.CASH_PAID_OTHER_OPERATING_EXPENSES,
    title: t('financialPlan.projectedCashFlow.direct.operatingActivities.cashPaidOtherOperatingExpenses'),
  },
  {
    name: OPERATING_ACTIVITIES_NAMES.INTEREST_RECEIVED,
    title: t('financialPlan.projectedCashFlow.direct.operatingActivities.interestReceived'),
  },
  {
    name: OPERATING_ACTIVITIES_NAMES.INTEREST_PAID,
    title: t('financialPlan.projectedCashFlow.direct.operatingActivities.interestPaid'),
  },
  {
    name: OPERATING_ACTIVITIES_NAMES.INCOME_TAXES_PAID,
    title: t('financialPlan.projectedCashFlow.direct.operatingActivities.incomeTaxesPaid'),
  },
];

export const getInvestingActivitiesRowsTitles = (isExamplePage: boolean) => {
  return [
    isExamplePage
      ? 'Purchase of intangible and other non-current assets'
      : t('financialPlan.projectedCashFlow.direct.investingActivities.purchaseIntangibleNonCurrentAssets'),
    isExamplePage
      ? 'Purchases of investments'
      : t('financialPlan.projectedCashFlow.direct.investingActivities.purchaseInvestments'),
    isExamplePage
      ? 'Proceeds from sale of property, plant, and equipment'
      : t('financialPlan.projectedCashFlow.direct.investingActivities.proceedsSalePropertyPlantEquipment'),
    isExamplePage
      ? 'Proceeds from sale or maturity of investments'
      : t('financialPlan.projectedCashFlow.direct.investingActivities.proceedsSaleMaturityInvestments'),
    isExamplePage
      ? 'Receipt of government grants'
      : t('financialPlan.projectedCashFlow.direct.investingActivities.receiptGovernmentGrants'),
  ];
};

export const getFinancingActivitiesRowsTitles = (isExamplePage: boolean) => {
  return [
    isExamplePage
      ? 'Repurchase of company shares'
      : t('financialPlan.projectedCashFlow.direct.financingActivities.repurchaseCompanyShares'),
    isExamplePage ? 'Dividends paid' : t('financialPlan.projectedCashFlow.direct.financingActivities.dividendsPaid'),
  ];
};

export const PROJECTED_CASH_FLOW_FORM_NAMES = {
  CURRENCY: 'currency',
  DIRECT: 'direct',
  IN_DIRECT: 'inDirect',
  OPERATING_ACTIVITIES: 'cashFlowsOperatingActivities',
  INVESTING_ACTIVITIES: 'cashFLowsInvestingActivities',
  FINANCING_ACTIVITIES: 'cashFLowsFinancingActivities',
  CASH_FLOWS_OPERATING_ACTIVITIES: 'cashFlowsOperatingActivities',
  ADJUSTMENTS_FOR: 'adjustmentsFor',
  OPERATING_ASSETS_AND_LIABILITIES_CHANGES: 'operatingAssetsAndLiabilitiesChanges',
  OTHER_ITEMS: 'otherItems',
  NET_CASH_FLOWS_OPERATING_CHANGE: 'netCashFlowsOperatingChange',
  CASH_FLOWS_INVESTING_STRUCTURE: 'cashFlowsInvestingStructure',
  NET_CASH_FLOW_INVESTING_CHANGE: 'netCashFlowInvestingChange',
  CASH_FLOWS_FINANCING_STRUCTURE: 'cashFlowsFinancingStructure',
  NET_CASH_FLOWS_FINANCING_CHANGE: 'netCashFlowsFinancingChange',
  SELECTED_TYPE: 'selectedType',
  AMORTIZATIONS: 'amortizations',
  DEPRECATION: 'deprecation',
} as const;

export const PROJECTED_CASH_FLOW_OPTION_NAMES = {
  DIRECT: 'direct',
  IN_DIRECT: 'inDirect',
} as const;

export const useGetProjectedCashFlowOptions = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const PROJECTED_CASH_FLOW_OPTIONS = [
      {
        label: t('financialPlan.projectedCashFlow.direct.title'),
        value: PROJECTED_CASH_FLOW_OPTION_NAMES.DIRECT,
      },
      {
        label: t('financialPlan.projectedCashFlow.inDirect.title'),
        value: PROJECTED_CASH_FLOW_OPTION_NAMES.IN_DIRECT,
      },
    ];
    return PROJECTED_CASH_FLOW_OPTIONS;
  }, [t, i18n]);
};

export type IProjectedCashFlowValuesType = ObjectValues<typeof PROJECTED_CASH_FLOW_OPTION_NAMES>;

export const projectedCashFlowOptionsToComponentMap = {
  [PROJECTED_CASH_FLOW_OPTION_NAMES.DIRECT]: DirectForm,
  [PROJECTED_CASH_FLOW_OPTION_NAMES.IN_DIRECT]: InDirectForm,
};

export const directFormPrefix = concatWithDot(projectedCashFlowPrefix, PROJECTED_CASH_FLOW_FORM_NAMES.DIRECT);
export const inDirectFormPrefix = concatWithDot(projectedCashFlowPrefix, PROJECTED_CASH_FLOW_FORM_NAMES.IN_DIRECT);

export const operatingActivitiesPrefix = concatWithDot(
  directFormPrefix,
  PROJECTED_CASH_FLOW_FORM_NAMES.OPERATING_ACTIVITIES,
);
export const investingActivitiesPrefix = concatWithDot(
  directFormPrefix,
  PROJECTED_CASH_FLOW_FORM_NAMES.INVESTING_ACTIVITIES,
);
export const financingActivitiesPrefix = concatWithDot(
  directFormPrefix,
  PROJECTED_CASH_FLOW_FORM_NAMES.FINANCING_ACTIVITIES,
);

export const adjustmentsForPrefix = concatWithDot(inDirectFormPrefix, PROJECTED_CASH_FLOW_FORM_NAMES.ADJUSTMENTS_FOR);
export const operatingAssetsAndLiabilitiesPrefix = concatWithDot(
  inDirectFormPrefix,
  PROJECTED_CASH_FLOW_FORM_NAMES.OPERATING_ASSETS_AND_LIABILITIES_CHANGES,
);
export const otherItemsPrefix = concatWithDot(inDirectFormPrefix, PROJECTED_CASH_FLOW_FORM_NAMES.OTHER_ITEMS);
export const selectedTabPrefix = concatWithDot(projectedCashFlowPrefix, PROJECTED_CASH_FLOW_FORM_NAMES.SELECTED_TYPE);

interface IInputListType {
  title: string;
  name: string;
}

export const useGetInputFieldsList = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const INPUT_FIELDS_LIST: IInputListType[] = [
      {
        title: t('financialPlan.projectedCashFlow.netCashFlowsOperatingChange'),
        name: PROJECTED_CASH_FLOW_FORM_NAMES.NET_CASH_FLOWS_OPERATING_CHANGE,
      },
      {
        title: t('financialPlan.projectedCashFlow.cashFlowsInvestingStructure'),
        name: PROJECTED_CASH_FLOW_FORM_NAMES.CASH_FLOWS_INVESTING_STRUCTURE,
      },
      {
        title: t('financialPlan.projectedCashFlow.netCashFlowInvestingChange'),
        name: PROJECTED_CASH_FLOW_FORM_NAMES.NET_CASH_FLOW_INVESTING_CHANGE,
      },
      {
        title: t('financialPlan.projectedCashFlow.cashFlowsFinancingStructure'),
        name: PROJECTED_CASH_FLOW_FORM_NAMES.CASH_FLOWS_FINANCING_STRUCTURE,
      },
      {
        title: t('financialPlan.projectedCashFlow.netCashFlowsFinancingChange'),
        name: PROJECTED_CASH_FLOW_FORM_NAMES.NET_CASH_FLOWS_FINANCING_CHANGE,
      },
    ];
    return INPUT_FIELDS_LIST;
  }, [t, i18n]);
};

export interface IProjectedCashFlow {
  [PROJECTED_CASH_FLOW_FORM_NAMES.DIRECT]: {
    [PROJECTED_CASH_FLOW_FORM_NAMES.INVESTING_ACTIVITIES]: IInvestingActivitiesType;
    [PROJECTED_CASH_FLOW_FORM_NAMES.FINANCING_ACTIVITIES]: IFinancingActivitiesType;
    [PROJECTED_CASH_FLOW_FORM_NAMES.OPERATING_ACTIVITIES]: IOperatingActivitiesType;
    [TOTAL_FIELDS.NET_INCREASE_DECREASE_CASH_EQUIVALENTS.name]: IPeriodsTableRow;
    [TOTAL_FIELDS.CASH_EQUIVALENTS_BEGINNING_PERIOD.name]: IPeriodsTableRow;
    [TOTAL_FIELDS.CASH_EQUIVALENTS_END_PERIOD.name]: IPeriodsTableRow;
  };
  [PROJECTED_CASH_FLOW_FORM_NAMES.IN_DIRECT]: {
    [PROJECTED_CASH_FLOW_FORM_NAMES.ADJUSTMENTS_FOR]: IAdjustmentsForInitialValues;
    [PROJECTED_CASH_FLOW_FORM_NAMES.OPERATING_ASSETS_AND_LIABILITIES_CHANGES]: IOperatingAssetsAndLiabilitiesInitialValues;
    [PROJECTED_CASH_FLOW_FORM_NAMES.OTHER_ITEMS]: IOtherItemsInitialValues;
    [INDIRECT_TOTAL_FIELDS.NET_CASH_PROVIDED_OPERATING_ACTIVITIES.name]: IPeriodsTableRow;
    [INDIRECT_TOTAL_FIELDS.NET_INCOME_PROFIT_FOR_PERIOD.name]: IPeriodsTableRow;
  };
  [PROJECTED_CASH_FLOW_FORM_NAMES.NET_CASH_FLOWS_OPERATING_CHANGE]: string;
  [PROJECTED_CASH_FLOW_FORM_NAMES.CASH_FLOWS_INVESTING_STRUCTURE]: string;
  [PROJECTED_CASH_FLOW_FORM_NAMES.NET_CASH_FLOW_INVESTING_CHANGE]: string;
  [PROJECTED_CASH_FLOW_FORM_NAMES.CASH_FLOWS_FINANCING_STRUCTURE]: string;
  [PROJECTED_CASH_FLOW_FORM_NAMES.NET_CASH_FLOWS_FINANCING_CHANGE]: string;
  [PROJECTED_CASH_FLOW_FORM_NAMES.SELECTED_TYPE]: IProjectedCashFlowValuesType;
  [PROJECTED_CASH_FLOW_FORM_NAMES.DEPRECATION]: IPeriodsTableRow;
  [PROJECTED_CASH_FLOW_FORM_NAMES.AMORTIZATIONS]: IPeriodsTableRow;
}

export const projectedCashFlowInitialValues: IProjectedCashFlow = {
  [PROJECTED_CASH_FLOW_FORM_NAMES.DIRECT]: {
    [PROJECTED_CASH_FLOW_FORM_NAMES.INVESTING_ACTIVITIES]: investingActivitiesInitialValues,
    [PROJECTED_CASH_FLOW_FORM_NAMES.FINANCING_ACTIVITIES]: financingActivitiesInitialValues,
    [PROJECTED_CASH_FLOW_FORM_NAMES.OPERATING_ACTIVITIES]: operatingActivitiesInitialValues,
    [TOTAL_FIELDS.NET_INCREASE_DECREASE_CASH_EQUIVALENTS.name]: initialPeriodsTableRow,
    [TOTAL_FIELDS.CASH_EQUIVALENTS_BEGINNING_PERIOD.name]: initialPeriodsTableRow,
    [TOTAL_FIELDS.CASH_EQUIVALENTS_END_PERIOD.name]: initialPeriodsTableRow,
  },
  [PROJECTED_CASH_FLOW_FORM_NAMES.IN_DIRECT]: {
    [PROJECTED_CASH_FLOW_FORM_NAMES.ADJUSTMENTS_FOR]: adjustmentsForInitialValues,
    [PROJECTED_CASH_FLOW_FORM_NAMES.OPERATING_ASSETS_AND_LIABILITIES_CHANGES]:
      operatingAssetsAndLiabilitiesInitialValues,
    [PROJECTED_CASH_FLOW_FORM_NAMES.OTHER_ITEMS]: otherItemsInitialValues,
    [INDIRECT_TOTAL_FIELDS.NET_CASH_PROVIDED_OPERATING_ACTIVITIES.name]: initialPeriodsTableRow,
    [INDIRECT_TOTAL_FIELDS.NET_INCOME_PROFIT_FOR_PERIOD.name]: initialPeriodsTableRow,
  },
  [PROJECTED_CASH_FLOW_FORM_NAMES.NET_CASH_FLOWS_OPERATING_CHANGE]: '',
  [PROJECTED_CASH_FLOW_FORM_NAMES.CASH_FLOWS_INVESTING_STRUCTURE]: '',
  [PROJECTED_CASH_FLOW_FORM_NAMES.NET_CASH_FLOW_INVESTING_CHANGE]: '',
  [PROJECTED_CASH_FLOW_FORM_NAMES.CASH_FLOWS_FINANCING_STRUCTURE]: '',
  [PROJECTED_CASH_FLOW_FORM_NAMES.NET_CASH_FLOWS_FINANCING_CHANGE]: '',
  [PROJECTED_CASH_FLOW_FORM_NAMES.SELECTED_TYPE]: PROJECTED_CASH_FLOW_OPTION_NAMES.DIRECT,
  [PROJECTED_CASH_FLOW_FORM_NAMES.DEPRECATION]: initialPeriodsTableRow,
  [PROJECTED_CASH_FLOW_FORM_NAMES.AMORTIZATIONS]: initialPeriodsTableRow,
};
